import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import PostParseModal from "../components/PostParseModal";

interface ParseIssue {
  severity: "critical" | "warning";
  message: string;
  action?: { label: string; path: string };
}

interface PostParseContextValue {
  /** Call after a successful parse to surface issues in the global modal. */
  showParseIssues: (dealId: string, issues: ParseIssue[]) => void;
}

const PostParseContext = createContext<PostParseContextValue>({
  showParseIssues: () => {},
});

export function usePostParse() {
  return useContext(PostParseContext);
}

export function PostParseProvider({ children }: { children: ReactNode }) {
  const [activeDealId, setActiveDealId] = useState<string | null>(null);
  const [issues, setIssues] = useState<ParseIssue[]>([]);

  const showParseIssues = useCallback((dealId: string, newIssues: ParseIssue[]) => {
    if (newIssues.length > 0) {
      setActiveDealId(dealId);
      setIssues(newIssues);
    }
  }, []);

  const dismiss = useCallback(() => {
    setIssues([]);
    setActiveDealId(null);
  }, []);

  return (
    <PostParseContext.Provider value={{ showParseIssues }}>
      {children}
      {issues.length > 0 && activeDealId && (
        <PostParseModal
          dealId={activeDealId}
          issues={issues}
          onDismiss={dismiss}
        />
      )}
    </PostParseContext.Provider>
  );
}
